import React, {Fragment, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {FormProvider, useFieldArray, useForm} from 'react-hook-form';
import styled from 'styled-components';
import Big from 'big.js';
import {joiResolver} from '@hookform/resolvers/joi';
import {generateReport} from "../../../../helpers/reports-helper";

import {HCheckbox, HDatePicker, HInput} from '../../../../validation/HForm';
import {ConfirmModal} from '../../../common';
import {dealService} from '../../../../services/deals-service';
import closeSchema from './closeModalSchema';
import {calculationAccess, getExpectedMessage, getUserFromLocalStorage, permissionsAccess, returnEndStr,} from '../../../../helpers/common';
import { SESSION_BY_ID } from '../../../../constants/token';
import { ModalInboxOperation } from '../../../Reports/modals';
import {Message, useRecalculation} from '../..';
import InfoPanel from './InfoPanel';
import {messages} from '../../../../helpers/messages';
import {basisForCloseAndProlongDeal, listRoles, paymentErrors} from '../../../../helpers/data-constants';
import useGlobalProlongDeal from "../ProlongModal/hooks/useGlobalProlongDeal";

import './closeModalLayout.scss';
import moment from 'moment';
import ForgiveDebtModal from "../ForgiveDebt/ForgiveDebt";
import NonCash from "../../NonCash/NonCash";
import PaymentTypeSelector from '../../PaymentSelector/PaymentTypeSelector';
import BasicAlert from "../../Alert/Alert";

const CheckboxPanel = styled.div`
  height: 25px;
  width: 35%;
  margin-left: 15px;

  .checkbox label span {
    font-size: 16px;
  }
`;

const AmountPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const Line = styled.hr`
  height: 1px;
  border: 0;
  background-color: #e5e5e5;
`;

const rowLoading = {
    dealId: '...',
    dealDays: '...',
    interestRate: '...',
    penaltyInterestRate: '...',
    delayDays: '...',
    interestAmount: '...',
    penaltyAmount: '...',
    principalAmount: '...',
    overpaymentTotal: '...',
    lostTicketFee: '...',
    totalCharge: '...',
    totalPenalty: '...',
};

const errorContentMaxDayForClosur = 'Достигнуто максимальное количество оплачиваемых дней';
const accessError = 'Ошибка! Недостаточно прав доступа.';

const rolesForClosureDeals = [listRoles.ADMIN, listRoles.GOODS_SPECIALIST];
const rolesForWaiverOfRedemptionDeals = [listRoles.ADMIN, listRoles.DIRECTOR];

const CloseModalLayout = (props) => {
    const {
        deals,
        handleCloseModal,
        openBuyThirdPersonModal,
        isBuyThirdPerson: {data},
        cashBoxId,
    } = props;
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isCloseDeal, setIsCloseDeal] = useState(false);
    const [basicAlertModal, setBasicAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState(paymentErrors.NON_CASH_ERROR);
    const [valueAfterSubmit, setValueAfterSubmit] = useState();
    const [checked, setChecked] = useState();
    const [calc, setCalc] = useState({
        deals: [],
        finalCharge: 0,
        change: 0,
    });
    const [error, setError] = useState('');
    const [disableButton, setDisableButton] = useState(false);
    const [currentModalIndex, setCurrentModalIndex] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [change, setChange] = useState(0);
    const [paymentInputs, setPaymentInputs] = useState([])
    const userRoles = getUserFromLocalStorage().roles;
    const showForClosureDeals = calculationAccess(rolesForClosureDeals);
    const accessWaiverOfRedemptionDeals = calculationAccess(rolesForWaiverOfRedemptionDeals);
    const {clientContract: {waiverOfRedemption, startDate},} = useSelector(({contract}) => contract);
    const {accessFeatures} = useSelector(({user}) => user);
    const minDate = calc.deals[0]?.minClosureDate;
    const userCashBox = JSON.parse(localStorage.getItem(SESSION_BY_ID))
    const isNonCashOperationCrediton = accessFeatures['non_cash.operations.type']
    const { isNewGlobalProlong } = useGlobalProlongDeal({deals, startDate});
    const cashValue = 'CASH';
    const nonCashValue = 'NON_CASH';

    const form = useForm({
        mode: 'all',
        defaultValues: {
            deals: deals.map((deal) => ({
                dealId: deal.id,
                isInterestAmountForgiven: false,
                isPenaltyAmountForgiven: false,
            })),
            deposited: 0,
            deposit: {
                cash: 0,
                nonCash: 0,
                bankTransaction: 0
            }
        },
        resolver: joiResolver(closeSchema),
    });

    const cash = form.watch('deposit.cash') || 0;
    const nonCash = form.watch('deposit.nonCash') || 0;
    const nonCashAccount = form.watch('deposit.nonCashAccount') || 0;
    const bankTransaction = form.watch('deposit.bankTransaction') || 0;
    const total = +cash + +nonCash + +nonCashAccount + +bankTransaction;
    const cashMinusChange = +cash - change
    const { recalculationDeals, isRecalculation, onRecalculationCloseContract } = useRecalculation(
        {calc, transactions, accessFeatures, valueAfterSubmit, cashMinusChange, nonCash, nonCashAccount, bankTransaction, handleCloseModal});

    const fieldArray = useFieldArray({
        control: form.control,
        name: 'deals',
        keyName: 'dealId',
    });

    useEffect(() => {
        refreshCalculations();
    }, [total]);

    useEffect(() => {
        setChecked(!!data);
    }, [data]);

    const refreshCalculations = () => {
        const values = form.getValues();

        const isClosureDateValid = isRecalculation && moment(values.closureDate).isAfter(moment());
        setDisableButton(isClosureDateValid);
        setError(isClosureDateValid ? messages[16631] : null);

        !values.deposited && (values.deposited = 0);
        values.closureDate = values?.closureDate
            ? values.closureDate
            : moment().format('YYYY-MM-DD')
        dealService
            .getCloseContractsCalculations(
                {
                    ...values,
                    deposit: {
                        cash: +values.deposit?.cash || 0,
                        nonCash: +values.deposit?.nonCash || 0,
                        nonCashAccount: +values.deposit?.nonCashAccount || 0,
                        bankTransaction: +values.deposit?.bankTransaction || 0,
                    }
                }
            )
            .then((resp) => {
                setCalc(resp.data);
            })
            .catch((err) => {
                if (err.response) {
                    let errorData = err.response.data;
                    let commonError = messages['0'];
                    if (errorData.errorCode) {
                        commonError = messages[errorData.errorCode];
                    }
                    setError(commonError);
                }
            });
    };

    useEffect(() => {
        const expectedTotal = isNonCashOperationCrediton 
        ? (total - change).toFixed(2) !== calc.finalCharge.toFixed(2) 
        : total.toFixed(2) !== calc.finalCharge.toFixed(2);
        
        if (expectedTotal) {
            setDisableButton(false)
            setError('')
            return;
        }
        if (!accessWaiverOfRedemptionDeals && waiverOfRedemption) {
            setDisableButton(true)
            return setError(accessError);
        }
        if (
            userRoles.includes(listRoles.GOODS_SPECIALIST) &&
            !userRoles.includes(listRoles.ADMIN) &&
            deals.find((i) => i.cashBoxId !== cashBoxId)
        ) {
            setDisableButton(true)
            return setError('Договор(а) из другого подразделения');
        }
    }, [accessWaiverOfRedemptionDeals, waiverOfRedemption, userRoles, deals, cashBoxId]);

    const onCloseContract = () => {
        setConfirmOpen(false);
        setIsCloseDeal(false);
        valueAfterSubmit.closureDate = valueAfterSubmit?.closureDate
            ? moment(valueAfterSubmit.closureDate).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD')
        dealService
            .closeContracts({
                ...valueAfterSubmit,
                deposit: {
                    cash: +cashMinusChange,
                    nonCash: +nonCash,
                    nonCashAccount: +nonCashAccount,
                    bankTransaction: +bankTransaction,
                }
            })
            .then((res) => {

                handleCloseModal(true);

                if (accessFeatures['report.closure_deal_receipt']) {
                    valueAfterSubmit.deals.forEach((deal, index) => generateReport('CLOSE_DEAL_RECEIPT', {
                        dealId: deal.dealId,
                        operationId: res.data.operationIds[index]
                    }));
                }

                openBuyThirdPersonModal(false);
            })
            .catch((err) => {
                if (err.response) {
                    let errorData = err.response.data;
                    let commonError = messages['0'];
                    if (errorData.errorCode) {
                        commonError = messages[errorData.errorCode];
                    }
                    setError(commonError);
                }
            });
    };

    function onSubmit(values) {
        const expectedTotal = isNonCashOperationCrediton 
        ? (total - change).toFixed(2) !== calc.finalCharge.toFixed(2) 
        : total.toFixed(2) !== calc.finalCharge.toFixed(2);

        const expectedMessage = getExpectedMessage(isNonCashOperationCrediton, paymentInputs)

        if (expectedTotal) {
            setBasicAlertModal(true)
            setAlertMessage(expectedMessage)
            return;
        }
       
        setValueAfterSubmit(values);

        if (data) {
            values.closureByAnotherPerson = data;
        }

        if(isRecalculation) {
            setCurrentModalIndex(0)
            return
        }

        setConfirmOpen(true);
        setIsCloseDeal(true);
    }

    function goBack() {
        setConfirmOpen(false);
        handleCloseModal();
    }

    const onChange = (e) => {
        e.persist();
        openBuyThirdPersonModal(e.target.checked);
    };

    const onCancelOperation = () => {
        setConfirmOpen(true);
        openBuyThirdPersonModal(false);
    };

    const onCloseModal = () => setConfirmOpen(false);

    const handleLocalCloseModal = () => {
        onCloseModal();
        setIsCloseDeal(false);
        setTransactions([])
    };

    const handleNextTransaction = (entity) => {
        let cash;
        if (entity?.payment === 'Наличный') cash = cashValue;
        if (entity?.payment === 'Безналичный') cash = nonCashValue;

        let data = {
            type: 'DEBIT',
            section: entity?.section.value,
            amount: entity?.amount,
            date: entity?.date,
            documentNumber: entity?.documentNumber,
            description: entity?.description,
            accepted: entity?.accepted.label,
            cash: cash,
            attachment: entity?.attachment,
            passport: entity?.passport,
            dealId: recalculationDeals[currentModalIndex].dealId
        }
        setTransactions((prev) => [...prev, data]);

        if (currentModalIndex < recalculationDeals.length - 1) {
            setCurrentModalIndex((prev) => prev + 1);   
        } else {
            setCurrentModalIndex(null);
            setConfirmOpen(true);
            setIsCloseDeal(true);
        }
    }

    const closeModelCreateTransaction = () => {
        if (currentModalIndex) {
            setCurrentModalIndex((prev) => prev - 1);   
        } else {
            setTransactions([])
            setCurrentModalIndex(null)
        }     
    }

    const showInfoMessage = () => {
        const messageText = `Расходный ордер ${currentModalIndex + 1} из ${recalculationDeals.length}`
        return recalculationDeals.length > 1 && <Message type="info" text={messageText}/>
    }

    const getHandleAction = () => {
        if (isCloseDeal) {
            return isRecalculation ? onRecalculationCloseContract : onCloseContract;
        }
        return goBack;
    }; 

    const microloan = calc.deals.reduce((p, c) => new Big(c.principalAmount).plus(p).toFixed(2), 0);

    const forUsing = calc.deals
        .filter(({recalculation}) => !recalculation)
        .reduce(
            (p, c) =>
                new Big(c.overpaymentTotal).plus(c.totalPenalty).plus(p).toFixed(2),
            0,
        );

    const lostTicketFee = calc.deals.find(({lostTicketFee}) => lostTicketFee !== 0)
        ? calc.deals.reduce((p, c) => new Big(c.lostTicketFee).plus(p).toFixed(2), 0)
        : null;

    const recalculationTotal = calc.deals.filter((i) => i?.recalculation).length
        ? calc.deals
            .filter((i) => i?.recalculation)
            .reduce((p, c) => new Big(c.recalculation).plus(p).toFixed(2), 0)
        : null;

    const getTitle = (deal) => {
        let baseTitle;
    
        if (accessFeatures['cashboxNameTypeOne']) {
            baseTitle = basisForCloseAndProlongDeal.CREDITON.BASE_EXPIRE;
        } else if (isNewGlobalProlong) {
            baseTitle = basisForCloseAndProlongDeal.VISEM.NEW_DELAY_EXPIRE;
        } else {
            baseTitle = basisForCloseAndProlongDeal.VISEM.BASE_EXPIRE;
        }
    
        return (<>{baseTitle}
            <b>№{deal.dealNumber || deal.dealId}</b> за{' '}
            {returnEndStr(deal.interestDays)} по {deal.dealInterestRate}%</>);
    };

    const formatAmount = (amount) => amount !== '...' ? amount.toFixed(2) : amount;
    const isValidValue = (value) => value !== 0 && value !== null && value !== '...';

    return (
        <>
            {error && <Message type="error" text={error}/>}
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="closeModalLayout-modalTitle">
                        <h2>Закрытие договора</h2>
                        {showForClosureDeals &&
                            <div className="closeModalLayout-datePicker">
                                <span className="closeModalLayout-span">Дата закрытия договора</span>
                                <HDatePicker
                                    className="input"
                                    name="closureDate"
                                    min={minDate}
                                    defaultValue={moment().format('YYYY-MM-DD')}
                                    onInput={refreshCalculations}
                                    readOnly={!permissionsAccess("deal.close.change_close_date")}
                                />
                            </div>
                        }
                    </div>

                    <div className="modal-body">
                        {fieldArray.fields.map((deal, idx) => {
                            const isInterestAmountForgiven = form.getValues(`deals[${idx}].isInterestAmountForgiven`);
                            const isPenaltyAmountForgiven = form.getValues(`deals[${idx}].isPenaltyAmountForgiven`);

                            const c = calc.deals[idx] || rowLoading;
                            const firstBasePercent = c.firstBasePercent ? c.firstBasePercent : c.dealInterestRate;

                            return (
                                <Fragment key={deal.dealId}>
                                    <div className="closeModalLayout-body-item">
                                        <HInput
                                            type="hidden"
                                            name={`deals[${idx}].dealId`}
                                            defaultValue={deal.dealId}
                                        />
                                        <HInput
                                            type="hidden"
                                            defaultValue={deal?.isInterestAmountForgiven}
                                            {...form.register(`deals[${idx}].isInterestAmountForgiven`)}
                                        />
                                        <HInput
                                            type="hidden"
                                            defaultValue={deal?.isPenaltyAmountForgiven}
                                            {...form.register(`deals[${idx}].isPenaltyAmountForgiven`)}
                                        />
                                        {(c.interestLimitReached ||
                                            c.overpaymentLimitReached ||
                                            c.penaltyLimitReached) && (
                                            <Message type="info" text={errorContentMaxDayForClosur}/>
                                        )}
                                         <InfoPanel
                                            isShowRows={!isNewGlobalProlong}
                                            title="Погашено микрозаймов"
                                            extra={`${formatAmount(c.principalAmount)} руб.`}
                                            rows={[{
                                                title: 'Микрозаем',
                                                value: formatAmount(c.principalAmount)
                                            }]}
                                        />
                                        {c.interestAmount !== 0 && (
                                            <InfoPanel
                                                show={isOverdue(c)}
                                                isShowRows={!isNewGlobalProlong}
                                                title={getTitle(c)}
                                                extra={accessFeatures['deal.forgive_debt'] && permissionsAccess('deal.forgive_debt')
                                                    ? <div className='forgiveDebt-valueDebt'>
                                                        <ForgiveDebtModal
                                                            forgiveValue={`${formatAmount(c.interestAmount)} руб.`}
                                                            isForgive={isInterestAmountForgiven}
                                                            submitForgive={() => {
                                                                form.setValue(`deals[${idx}].isInterestAmountForgiven`, true);
                                                                refreshCalculations();
                                                            }}
                                                            closeForgive={() => {
                                                                form.setValue(`deals[${idx}].isInterestAmountForgiven`, false);
                                                                refreshCalculations();
                                                            }}
                                                        />
                                                    </div>
                                                    : `${formatAmount(c.interestAmount)} руб.`
                                                }
                                                rows={[
                                                    {
                                                        title: 'Проценты за неустойку',
                                                        value: isInterestAmountForgiven
                                                            ? '0.00'
                                                            : formatAmount(c.interestAmount)
                                                    },
                                                    {title: 'НДС', value: 0},
                                                ]}
                                            />
                                        )}
                                        {
                                            isValidValue(c.overpaymentTotal) &&
                                            <InfoPanel
                                                isShowRows={!isNewGlobalProlong}
                                                title={<>{"Проценты за пользование микрозаймом "}
                                                    {isNewGlobalProlong && (
                                                    <>
                                                        <b>№{c.dealNumber || c.dealId}</b>
                                                        {` за ${returnEndStr(c.dealDays)} по ${isNewGlobalProlong ? firstBasePercent : c.dealInterestRate}%`}
                                                    </>
                                                )}</>}
                                                extra={`${c.overpaymentTotal.toFixed(2)} руб.`}
                                                rows={[
                                                    {
                                                        title: 'За пользование микрозаймом',
                                                        value: c?.overpaymentTotal
                                                    },
                                                ]}
                                            />
                                        }
                                        {c.penaltyAmount !== 0 && (
                                            isNewGlobalProlong ? <InfoPanel
                                                panelKey={1} 
                                                show={isOverdue(c)}
                                                title={<>{basisForCloseAndProlongDeal.PENALTY}
                                                        <b>№{c.dealNumber || c.dealId}</b> за{' '}
                                                        {returnEndStr(c.totalPenaltyDays)} по {c.penaltyInterestRate}%</>}
                                                        extra={accessFeatures['deal.forgive_debt'] && permissionsAccess('deal.forgive_debt')
                                                            ? <div className='forgiveDebt-valueDebt'>
                                                                <ForgiveDebtModal
                                                                    forgiveValue={`${formatAmount(c.penaltyAmount)} руб.`}
                                                                    isForgive={isPenaltyAmountForgiven}
                                                                    submitForgive={() => {
                                                                        form.setValue(`deals[${idx}].isPenaltyAmountForgiven`, true);
                                                                        refreshCalculations();
                                                                    }}
                                                                    closeForgive={() => {
                                                                        form.setValue(`deals[${idx}].isPenaltyAmountForgiven`, false);
                                                                        refreshCalculations();
                                                                    }}
                                                                />
                                                            </div>
                                                            : `${formatAmount(c.penaltyAmount)} руб.`
                                                        }
                                                        rows={c.penalties?.map(({ dateFrom, dateTo, penalty, days }) => ({
                                                        title: <span className='closeModalLayout-penalties'>
                                                                {`${moment(dateFrom).format("DD.MM.YYYY")} - ${moment(dateTo).format("DD.MM.YYYY")} за ${returnEndStr(days)}`}
                                                            </span>,
                                                        value: <span className='closeModalLayout-penalties'>{penalty.toFixed(2)}</span>
                                                    }))
                                                }
                                            /> 
                                            : <InfoPanel
                                                show={isOverdue(c)}
                                                title={
                                                    <>
                                                        {accessFeatures['cashboxNameTypeOne'] ? basisForCloseAndProlongDeal.CREDITON.DELAY_EXPIRE : basisForCloseAndProlongDeal.VISEM.DELAY_EXPIRE}
                                                        <b>№{c.dealNumber || c.dealId}</b> за{' '}
                                                        {returnEndStr(c.delayDays)} по {c.penaltyInterestRate}%
                                                    </>
                                                }
                                                extra={accessFeatures['deal.forgive_debt'] && permissionsAccess('deal.forgive_debt')
                                                    ? <div className='forgiveDebt-valueDebt'>
                                                        <ForgiveDebtModal
                                                            forgiveValue={`${formatAmount(c.penaltyAmount)} руб.`}
                                                            isForgive={isPenaltyAmountForgiven}
                                                            submitForgive={() => {
                                                                form.setValue(`deals[${idx}].isPenaltyAmountForgiven`, true);
                                                                refreshCalculations();
                                                            }}
                                                            closeForgive={() => {
                                                                form.setValue(`deals[${idx}].isPenaltyAmountForgiven`, false);
                                                                refreshCalculations();
                                                            }}
                                                        />
                                                    </div>
                                                    : `${formatAmount(c.penaltyAmount)} руб.`
                                                }
                                                rows={[{
                                                        title: 'Проценты за неустойку',
                                                        value: isPenaltyAmountForgiven
                                                            ? '0.00'
                                                            : formatAmount(c.penaltyAmount)
                                                    },
                                                    {title: 'НДС', value: 0},
                                                ]}
                                            />)
                                        }
                                        {
                                            isValidValue(c.recalculation) &&
                                            <InfoPanel
                                                title={
                                                    <span
                                                        className={accessFeatures['deal.closure.recalculation_remind'] && 'closeModalLayout-recalculation'}>Перерасчет по микрозайму</span>}
                                                extra={
                                                    <span
                                                        className={accessFeatures['deal.closure.recalculation_remind'] && 'closeModalLayout-recalculation'}>
                                                        {c.recalculation && c.recalculation.toFixed(2)} руб.
                                                    </span>
                                                }
                                            />
                                        }
                                        <InfoPanel
                                            show={c.lostTicketFee > 0}
                                            title={'Выдача дубликата утерянного билета'}
                                            extra={`${formatAmount(c.lostTicketFee)} руб.`}
                                            rows={[
                                                {
                                                    title: 'Сумма комиссии',
                                                    value: formatAmount(c.lostTicketFee)
                                                },
                                                {title: 'НДС', value: 0},
                                            ]}
                                        />
                                        <InfoPanel
                                            title={<b>№{c.dealNumber || c.dealId}</b>}
                                            extra={`${formatAmount(c.totalCharge)} руб.`}
                                        />
                                        {
                                            accessFeatures["ticketLost"] &&
                                            <div className='closeModalLayout-lossOfReceipts'>
                                                <CheckboxPanel>
                                                    <HCheckbox
                                                        label={`Утеря квитанции`}
                                                        name={`deals[${idx}].ticketLost`}
                                                        defaultValue={deal.ticketLost}
                                                        onChange={refreshCalculations}
                                                    />
                                                </CheckboxPanel>
                                            </div>
                                        }
                                    </div>
                                    <Line/>
                                    <br/>
                                </Fragment>
                            );

                        })}
                        <CheckboxPanel>
                            <div className="checkbox">
                                <label>
                                    <input type="checkbox" checked={checked} onClick={onChange}/>
                                    <span>Выкуп третьим лицом</span>
                                </label>
                            </div>
                        </CheckboxPanel>
                        {!isNonCashOperationCrediton &&
                            <InfoPanel
                                title={
                                    <p className="font-size-larger font-color-red m-none">
                                        <b>Взять с клиента</b>
                                    </p>
                                }
                                extra={
                                    <p className="font-size-larger font-color-red m-none">
                                        <b>{calc.finalCharge !== 0 ? calc.finalCharge.toFixed(2) : calc.finalCharge} руб.</b>
                                    </p>
                                }
                                rows={[
                                    {title: 'Микрозаем', value: microloan},
                                    {title: 'За пользование микрозаймом', value: forUsing},
                                    {title: 'Перерасчёт по микрозаймам', value: recalculationTotal},
                                    {title: 'Выдача дубликата утерянного билета', value: lostTicketFee},
                                ]}
                            />
                        }

                        {isNonCashOperationCrediton 
                            ? 
                            <fieldset>
                                <PaymentTypeSelector
                                    form={form}
                                    maxTotalSum={calc.finalCharge.toFixed(2)} 
                                    calculateTotalSum={total}
                                    inputFieldName={'deposit'}
                                    setCashChangePayment={setChange} 
                                    setPaymentInputs={setPaymentInputs} />
                            </fieldset>
                            : <NonCash refreshCalculations={refreshCalculations} />}
                        

                        {!isNonCashOperationCrediton &&
                            <AmountPanel>
                                <h3>Итого внесено:</h3>
                                <h3>{total.toFixed(2) || 0}</h3>
                            </AmountPanel>
                        }
                    </div>
                    <div className="modal-footer clientContract-modal-footer">
                        <button className="button-light" type="button" onClick={onCancelOperation}>
                            Отменить
                        </button>
                        {
                            permissionsAccess('accept.money') &&
                            <button className={disableButton ? "button-light" : "button"} type="submit"
                                    disabled={disableButton}
                            >
                                Принять деньги
                            </button>
                        }
                    </div>
                    <BasicAlert onClose={() => setBasicAlertModal(false)}
                                message={alertMessage}
                                isOpen={basicAlertModal}
                                isError={true}
                    />
                    <ConfirmModal
                        isOpen={confirmOpen}
                        handleClose={isCloseDeal ? handleLocalCloseModal : onCloseModal}
                        handleAction={getHandleAction()}
                        headerText={
                            isCloseDeal ? 'Выполнить закрытие договора?' : 'Отменить закрытие договора?'
                        }
                        bodyText={isCloseDeal ? '' : 'Внесённые данные не сохранятся.'}
                    />
                </form>
            </FormProvider>
            {currentModalIndex !== null && isRecalculation && (
                <ModalInboxOperation
                    isOpen={true}
                    cashBoxName={userCashBox.label}
                    type={"DEBIT"}
                    transactionData={recalculationDeals[currentModalIndex]}
                    recalculation={!!isRecalculation}
                    onTransactionSubmit={handleNextTransaction}
                    closeModelCreateTransaction={closeModelCreateTransaction}
                    messageInfoElement={showInfoMessage()}
                />
            )}
        </>
    );
};

function isOverdue(calc) {
    return calc ? Number.isInteger(calc.delayDays) : false;
}

export default CloseModalLayout;
